import { FallbackSectionErrorBoundary } from "@/components/FallbackErrorBoundary";
import { ConsentStatus } from "@/features/consent/ConsentStatus";
import { useModals } from "@/features/modals/hooks";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { type Opportunity, useTeam } from "@repo/api";
import {
  Icon,
  LightMode,
  ListView,
  ListViewItem,
  type ListViewItemProps,
  folderIcon,
  locationPinSmallIcon,
  pencilIcon,
} from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import {
  ClickableDiv,
  CustomerCardWrapper,
  ElementsContainer,
  FlexDiv,
  LocalizedDate,
  OneLineInfo,
  PaddedContainer,
  ProjectTitle,
  TwoColumnSectionContainer,
  translateClassification,
  translateHFB,
  translateServices,
  useCoWorkerName,
  useCustomer,
  useServices,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

import { useActiveProject } from "./ActiveProject";
import { NeedAnalysisListViewItem } from "./NeedAnalysisListViewItem";
import { STAGE_ACTIONS_SECTION_IDS } from "./projectTopSection/stages/config";

export const ProjectDetailsItem = ({
  opportunity,
  control,
  onClick,
  showDivider = true,
  showProjectId,
}: {
  opportunity: Opportunity;
  control?: ListViewItemProps["control"];
  onClick?: () => void;
  showDivider?: boolean;
  showProjectId?: boolean;
}) => {
  const { market, language, general } = useSettings();
  const { customer } = useCustomer(opportunity.id);
  const services = useServices();

  return (
    <ListViewItem
      leadingIcon={folderIcon}
      control={control}
      onClick={onClick}
      title={<ProjectTitle title={opportunity.title} customer={customer} />}
      description={
        showProjectId ? `${t("label.id")}: ${opportunity.id}` : undefined
      }
      showDivider={showDivider}
      addOn={
        <div>
          <OneLineInfo
            divider=", "
            info={
              opportunity.servicesInterested
                ? opportunity.servicesInterested.map((h) =>
                    translateServices(services?.[h])
                  )
                : []
            }
          />
          <OneLineInfo
            info={[
              opportunity.customerBudget
                ? currencyFormatter({
                    value: Number(
                      opportunity.customerBudget?.replace(",", ".")
                    ),
                    currency: opportunity.currency,
                    locale: getLanguageLocal(language, market),
                    showSign: true,
                    disableDecimals: Boolean(
                      general?.localization?.disable_decimals
                    ),
                    fractions: 2,
                  })
                : t("prompt.no_budget"),
              opportunity.expectedPurchase ? (
                <LocalizedDate
                  date={`${opportunity.expectedPurchase}T12:00:00`}
                />
              ) : (
                t("prompt.no_purchase_date")
              ),
              translateClassification(opportunity.classification),
            ]}
            small
          />
        </div>
      }
    />
  );
};

export const ProjectDetailsSection = () => {
  const { pushModal } = useModals();
  const { consent_requirements } = useSettings();
  const { opportunity } = useActiveProject();
  const { name: coWorkerName } = useCoWorkerName(
    opportunity?.owner,
    opportunity?.owner
  );
  const { data: assignedTeam } = useTeam(opportunity?.assignedTeam);
  if (!opportunity) {
    return null;
  }
  const assignedTo =
    coWorkerName ?? assignedTeam?.teamName ?? t("label.unassigned");

  return (
    <PaddedContainer backgroundColor={LightMode.colourNeutral1}>
      <TwoColumnSectionContainer>
        <ElementsContainer>
          <CustomerCardWrapper projectId={opportunity.id} />
          <ClickableDiv
            id={STAGE_ACTIONS_SECTION_IDS.CONSENT_SECTION}
            onClick={() =>
              pushModal({
                type: "CONSENT_SETTINGS",
              })
            }
          >
            <FlexDiv gap="10px">
              <ConsentStatus
                consentType="FOLLOWUP_CONSENT"
                displayConsentType
              />
              {consent_requirements?.promotional_consent && (
                <ConsentStatus
                  consentType="PROMOTIONAL_CONSENT"
                  displayConsentType
                />
              )}
              <Icon paths={pencilIcon} />
            </FlexDiv>
          </ClickableDiv>
        </ElementsContainer>

        <ListView id="project-details-list">
          <ProjectDetailsItem
            opportunity={opportunity}
            onClick={() => {
              pushModal({ type: "NEED_ANALYSIS" });
              trackAnalytics("open_need_analysis_modal");
            }}
            control="navigational"
          />
          <ListViewItem
            leadingIcon={locationPinSmallIcon}
            control="navigational"
            title={assignedTo}
            description={
              <div title={t("label.customer")}>
                <OneLineInfo
                  info={[
                    opportunity.buName
                      ? `${opportunity.buName} ${opportunity.buCode}`
                      : undefined,
                    opportunity.customerMeetingPointBuName,
                  ]}
                />
                <OneLineInfo
                  info={[
                    opportunity.hfb
                      ? opportunity.hfb.map((h) => translateHFB(h)).join(", ")
                      : undefined,
                  ]}
                  small
                />
              </div>
            }
            onClick={() => {
              pushModal({ type: "PROJECT_DETAILS" });
              trackAnalytics("open_project_details_modal");
            }}
          />

          <FallbackSectionErrorBoundary
            errorMessage={t("prompt.failed_to_load_questionnaire")}
          >
            <NeedAnalysisListViewItem />
          </FallbackSectionErrorBoundary>
        </ListView>
      </TwoColumnSectionContainer>
    </PaddedContainer>
  );
};
